import React, { useEffect, useState } from 'react';
import { string, object, array, bool, number } from 'prop-types';
import { isEmpty } from 'lodash';
import { loadable } from '@fiverr-private/perseus';
import { Container } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';

import GigCardListings from '@fiverr-private/gig_listings/src/entries/GigCardListings';
import ListViewListings from '@fiverr-private/gig_listings/src/entries/ListViewListings';
import { api as collectApi } from '@fiverr-private/collect';
import { gigContextEnrichment } from '../../../utils/gigContextEnrichment';
import { sendMixpanelTrackEvent } from '../../../lib/mixpanel';
import { PAGE_VIEWED } from '../../../lib/mixpanel/constants';
import { Header } from '../Header';
import { Pagination } from '../Pagination';
import TotalGigsCount from '../TotalGigsCount';
import CertifiedBannerWrapper, { CertifiedBannerDataPropTypes } from '../CertifiedBannerWrapper';
import ErrorBoundary from '../ErrorBoundary';

const Reviews = loadable(() => import(/* webpackChunkName: "Reviews" */ '../Reviews'), {
    ssr: false,
});

const RelatedLinks = loadable(() => import(/* webpackChunkName: "RelatedLinks" */ '../RelatedLinks'));
const MachineTranslationButtonContainer = loadable(
    () => import(/* webpackChunkName: "MachineTranslationButtonContainer" */ '../MachineTranslationButtonContainer'),
    {
        ssr: false,
    }
);

const TagPage = ({
    name,
    gigs,
    shouldShowTranslatedGigs,
    currency,
    currentPage,
    numOfGigs,
    gigsActiveExperiments,
    reviewsGigIds,
    relatedLinks,
    certifiedBanner,
    heading,
    tagling,
    slug,
}) => {
    const [currentGigs, setCurrentGigs] = useState(gigs);

    const [shouldShowTranslatedUgc, setShouldShowTranslatedUgc] = useState(shouldShowTranslatedGigs);

    const { isTouch, userId } = getContext();

    const [collections, setCollections] = useState([]);

    useEffect(() => {
        const fetchCollections = async () => {
            if (userId) {
                const { collections } = await collectApi.getCollections();
                setCollections(collections);
            }
        };

        fetchCollections();
    }, [userId]);

    useEffect(() => {
        sendMixpanelTrackEvent({ eventName: PAGE_VIEWED });
    }, []);

    const enrichedGigContext = (source) =>
        gigContextEnrichment({
            collections,
            setCollections,
            currency,
            source,
            activeExperiments: gigsActiveExperiments,
        });

    const shouldRenderCertifiedBanner = !isEmpty(certifiedBanner);

    const banners = [
        shouldRenderCertifiedBanner && (
            <CertifiedBannerWrapper
                key="certified-banner"
                tagName={name}
                description={certifiedBanner.description}
                cta={certifiedBanner.cta}
                subCategoryId={certifiedBanner.subCategoryId}
                promoImageUrl={certifiedBanner.promoImageUrl}
                logoUrl={certifiedBanner.logoUrl}
            />
        ),
    ].filter(Boolean);

    return (
        <Container as="section" padding={isTouch ? '4' : '8'} maxWidth="1400px" marginX="auto">
            <ErrorBoundary>
                <Header name={name} heading={heading} tagling={tagling} />
                <Container alignItems="center" paddingBottom="4" gap="3">
                    {!isTouch && <TotalGigsCount total={numOfGigs} />}
                    <MachineTranslationButtonContainer
                        gigs={currentGigs}
                        setShouldShowTranslatedUgc={setShouldShowTranslatedUgc}
                        shouldShowTranslatedGigs={shouldShowTranslatedGigs}
                        toggled={shouldShowTranslatedUgc}
                        setCurrentGigs={setCurrentGigs}
                    />
                </Container>

                <Container
                    as="section"
                    borderColor="grey_500"
                    borderTopWidth="sm"
                    paddingTop="8"
                    paddingBottom={isTouch ? '4' : '10'}
                >
                    {isTouch ? (
                        <ListViewListings
                            showTranslatedUGC={shouldShowTranslatedUgc}
                            gigs={currentGigs}
                            banners={banners}
                            {...enrichedGigContext()}
                        />
                    ) : (
                        <GigCardListings
                            isExperiential
                            showTranslatedUGC={shouldShowTranslatedUgc}
                            gigs={currentGigs}
                            banners={banners}
                            gigTitleTag="h3"
                            {...enrichedGigContext()}
                        />
                    )}
                    <Pagination currentPage={currentPage} numOfGigs={numOfGigs} />
                </Container>

                <Reviews
                    gigListingContext={enrichedGigContext('tag_page_gig_with_reviews')}
                    reviewsGigIds={reviewsGigIds}
                    slug={slug}
                    name={name}
                    gigs={gigs}
                />
                {!isEmpty(relatedLinks) && <RelatedLinks relatedLinks={relatedLinks} name={name} />}
            </ErrorBoundary>
        </Container>
    );
};

TagPage.propTypes = {
    name: string,
    gigs: array,
    currency: object,
    currentPage: number,
    numOfGigs: number,
    gigsActiveExperiments: object,
    relatedLinks: array,
    reviewsGigIds: array,
    shouldShowTranslatedGigs: bool,
    certifiedBanner: CertifiedBannerDataPropTypes,
    heading: string,
    tagling: string,
    slug: string,
};

export default TagPage;
